import type { RichTextBlockPayload } from '@opinix-cloud/shared';
import { Logo } from '../components/Logo';

interface FooterProps {
  /**
   * Optional content. The Footer is rendered globally by App.tsx — at
   * mount it doesn't yet have content, so the `body` prop is optional
   * and defaults to the bundled tagline. The page-level fetcher hands
   * over the live copy on first paint.
   */
  content?: RichTextBlockPayload;
}

const DEFAULT_BODY =
  'Simulation and training built for the people who respond first.';

export function Footer({ content }: FooterProps): JSX.Element {
  const body = content?.body ?? DEFAULT_BODY;
  return (
    <footer className="bg-navy-950 text-steel-300">
      <div className="container-x py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2">
            <Logo />
            <p className="mt-4 text-sm text-steel-400 max-w-xs leading-relaxed">
              {body}
            </p>
            <p className="mt-6 text-xs text-steel-500">
              &copy; {new Date().getFullYear()} Opinix. All rights reserved.
            </p>
          </div>
          <FooterColumn
            title="Platform"
            links={[
              { label: 'Build', href: '#platform' },
              { label: 'Run', href: '#platform' },
              { label: 'Review', href: '#platform' },
              { label: 'Deployment', href: '#deployment' },
            ]}
          />
          <FooterColumn
            title="Verticals"
            links={[
              { label: 'Law enforcement', href: '#verticals' },
              { label: 'Schools', href: '#verticals' },
              { label: 'Healthcare', href: '#verticals' },
              { label: 'Emergency mgmt', href: '#verticals' },
            ]}
          />
        </div>
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-xs text-steel-500">
          <div className="flex items-center gap-6">
            <a href="/admin/" className="hover:text-white transition-colors">
              Sign in
            </a>
            <a href="#early-access" className="hover:text-white transition-colors">
              Early access
            </a>
          </div>
          <div className="font-mono">opinix-us.com</div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: { label: string; href: string }[];
}): JSX.Element {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white">
        {title}
      </h4>
      <ul className="mt-4 space-y-2.5">
        {links.map((l) => (
          <li key={l.label}>
            <a
              href={l.href}
              className="text-sm text-steel-400 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
